import { FONT_LIST } from './defaults';

// Schema-driven editor controls. The editor renders these dynamically so
// future presets can be added without modifying OpenerEditorPanel.
// Order here = order in the Studio sidebar.
//
// liveUpdate: true  → applies in-place via CSS vars / state.
// liveUpdate: false → updates draft only; UI surfaces "Replay to apply".

export const schema = [
  // Phase 1.9.15 — Editor order: text → opening cadence → exit cadence →
  // final cadence → ripples → typography → background.
  { id: 'openingText', type: 'textarea', label: 'Opening text', default: '', liveUpdate: false, maxLength: 200, hint: 'Three lines work best.' },

  { id: 'revealSpeed', type: 'range', label: 'Opening text speed', default: 40, min: 20, max: 80, step: 5, liveUpdate: false, format: 'ms', hint: 'Faster ← → Slower (ms per letter).' },

  { id: 'holdTime',    type: 'range', label: 'Hold time',         default: 4200, min: 1500, max: 8000, step: 100, liveUpdate: false, format: 'ms', hint: 'How long the text stays before dissolving.' },

  {
    // Phase 1.9.14 — "Up" added and set as default for the restrained
    // editorial dissolve. Existing values still resolve correctly via
    // getFlyValues() in the component.
    id: 'flyDirection', type: 'select', label: 'Text exit direction', default: 'up', liveUpdate: false,
    options: [
      { value: 'up',         label: 'Up' },
      { value: 'up-right',   label: 'Up right' },
      { value: 'up-left',    label: 'Up left' },
      { value: 'down-right', label: 'Down right' },
      { value: 'down-left',  label: 'Down left' },
      { value: 'soft-out',   label: 'Soft outward' },
    ],
  },

  { id: 'flyDuration', type: 'range', label: 'Text exit speed', default: 2400, min: 1400, max: 3200, step: 100, liveUpdate: false, format: 'ms', hint: 'Faster ← → Slower (fly-away duration).' },

  { id: 'finalText',   type: 'text',     label: 'Final text',   default: '', liveUpdate: false, maxLength: 80 },

  { id: 'finalFadeDuration', type: 'range', label: 'Final text fade', default: 2200, min: 800,  max: 3000, step: 100, liveUpdate: false, format: 'ms' },
  { id: 'finalHoldTime',     type: 'range', label: 'Final text hold', default: 2200, min: 1200, max: 5000, step: 100, liveUpdate: false, format: 'ms', hint: 'How long the final text remains before the invitation reveal.' },

  // Phase 1.9.13 — Water ripples are an OPTIONAL effect. Off by default.
  { id: 'waterRipplesEnabled', type: 'toggle', label: 'Water ripple animation', default: false, liveUpdate: true, hint: 'Adds gentle drops, ripples, and a water-lens distortion to the cotton paper.' },

  // The three controls below are part of the water-ripple system. They
  // are HIDDEN in the editor when waterRipplesEnabled is false, and the
  // public render also skips them. Their saved values are preserved so
  // toggling the effect back on restores the previous configuration.
  { id: 'rainDensity',        type: 'range', label: 'Rain density',      default: 1,    min: 0.4,  max: 1.6,  step: 0.05, liveUpdate: false, dependsOn: 'waterRipplesEnabled' },
  { id: 'rippleDurationScale',type: 'range', label: 'Ripple speed',      default: 1,    min: 0.6,  max: 1.4,  step: 0.05, liveUpdate: true,  dependsOn: 'waterRipplesEnabled', hint: '< 1 faster, > 1 slower.' },
  { id: 'distortionStrength', type: 'range', label: 'Water distortion',  default: 0,    min: 0,    max: 8,    step: 1,    liveUpdate: true,  dependsOn: 'waterRipplesEnabled' },

  {
    id: 'typography', type: 'group', label: 'Typography', default: null, liveUpdate: true,
    children: [
      { id: 'openingTypography.fontFamily', type: 'font',  label: 'Opening font',       default: 'Cormorant Garamond', fontList: FONT_LIST, liveUpdate: true },
      { id: 'openingTypography.color',      type: 'color', label: 'Opening text color', default: '#3b342c',            liveUpdate: true },
      { id: 'openingTypography.sizeScale',  type: 'range', label: 'Opening text size',  default: 1,  min: 0.7, max: 1.5, step: 0.05, liveUpdate: true },
      { id: 'finalTypography.fontFamily',   type: 'font',  label: 'Final font',         default: 'Cormorant Garamond', fontList: FONT_LIST, liveUpdate: true },
      { id: 'finalTypography.color',        type: 'color', label: 'Final text color',   default: '#3b342c',            liveUpdate: true },
      { id: 'finalTypography.sizeScale',    type: 'range', label: 'Final text size',    default: 1,  min: 0.7, max: 1.5, step: 0.05, liveUpdate: true },
    ],
  },

  {
    id: 'background', type: 'group', label: 'Background', default: null, liveUpdate: true,
    children: [
      { id: 'backgroundColor',    type: 'color', label: 'Paper tint', default: '#ebe7df', liveUpdate: true },
      { id: 'backgroundOpacity',  type: 'range', label: 'Background opacity', default: 0.94, min: 0.50, max: 1, step: 0.01, liveUpdate: true, format: 'percent', hint: 'Lets the invitation softly show through. Text stays sharp.' },
      { id: 'backdropBlur',       type: 'range', label: 'Backdrop blur',      default: 0,    min: 0,    max: 8, step: 1,    liveUpdate: true, format: 'px',      hint: 'Softens the invitation behind the paper. 0 keeps it crisp.' },
      // Phase 1.9.16 — declarative side effects. Keeps the generic image
      // control preset-agnostic: Rain still flips backgroundMode on
      // upload/remove via these declarations; other presets can omit them.
      { id: 'backgroundImageUrl', type: 'image', label: 'Background image', default: null, liveUpdate: true, uploadTag: 'opener_background', accept: 'image/*', hint: 'Layers under the paper texture.',
        onUploadSet: { backgroundMode: 'image' },
        onRemoveSet: { backgroundMode: 'paper' } },
    ],
  },
];
